import { ApplicationRef, Injectable, InjectionToken, OnDestroy, Optional, Inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { first, switchMap, takeUntil } from 'rxjs/operators';
import { EtagService } from '../etag/etag.service';

export const UPDATE_POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Overridable in tests so specs do not have to replace ApplicationRef. */
export const APP_STABLE = new InjectionToken<Observable<boolean>>('APP_STABLE');

/**
 * Detects new deployments by comparing the index.html ETag and drives the
 * "a newer version is available" prompt.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  readonly updateAvailable = new BehaviorSubject<boolean>(false);

  private readonly destroyed$ = new Subject<void>();
  private initialised = false;

  constructor(
    private readonly appRef: ApplicationRef,
    private readonly etagService: EtagService,
    @Optional()
    @Inject(APP_STABLE)
    private readonly appStable$: Observable<boolean> | null,
  ) {}

  init(): void {
    if (this.initialised) {
      return;
    }
    this.initialised = true;

    this.removeAngularServiceWorker();
    this.pollOnceStable$()
      .pipe(
        switchMap(() => this.etagService.hasNewVersion()),
        takeUntil(this.destroyed$),
      )
      .subscribe((hasNewVersion) => {
        if (hasNewVersion) {
          this.updateAvailable.next(true);
        }
      });
  }

  private pollOnceStable$() {
    const appIsStable$ = (this.appStable$ ?? this.appRef.isStable).pipe(
      first((isStable) => isStable),
    );

    return appIsStable$.pipe(
      switchMap(() => timer(0, UPDATE_POLL_INTERVAL_MS)),
      takeUntil(this.destroyed$),
    );
  }

  dismiss(): void {
    this.updateAvailable.next(false);
  }

  /**
   * Reloads after the user accepts the detected version.
   */
  applyUpdate(): void {
    this.etagService.acceptCurrentVersion().subscribe({
      next: () => this.reload(),
      error: (error) => {
        console.error('Failed to accept the current app version', error);
        this.reload();
      },
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private removeAngularServiceWorker(): void {
    const serviceWorker = navigator.serviceWorker;
    if (!serviceWorker) {
      return;
    }

    serviceWorker
      .getRegistrations()
      .then((registrations) =>
        Promise.all(
          registrations
            .filter((registration) =>
              registration.active?.scriptURL.endsWith('/ngsw-worker.js'),
            )
            .map((registration) => registration.unregister()),
        ),
      )
      .catch((error) =>
        console.error('Failed to unregister the Angular service worker', error),
      );
  }

  private reload(): void {
    this.updateAvailable.next(false);
    document.location.reload();
  }
}
